import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendAgentMessage, RateLimitError } from '../services/agentService';
import type { Message, ToolCall, ToolCallResult, AgentToastProps, UIFocus } from '../types/agent';
import { dispatchAgentRefresh } from '../hooks/useAgentRefresh';

// Custom event for focus
export const AGENT_FOCUS_EVENT = 'agent:focus';

export function dispatchAgentFocus(focus: UIFocus) {
  const event = new CustomEvent(AGENT_FOCUS_EVENT, { detail: focus });
  window.dispatchEvent(event);
}

// Custom event for tour start
export const AGENT_TOUR_START_EVENT = 'agent:tour:start';

export function dispatchAgentTourStart(tourId: string) {
  const event = new CustomEvent(AGENT_TOUR_START_EVENT, { detail: { tourId } });
  window.dispatchEvent(event);
}

interface SelectedEntity {
  id: string;
  type: 'lead' | 'todo' | 'form' | 'page';
}

interface AIAgentContextType {
  // Sidebar state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;

  // Chat state
  messages: Message[];
  isLoading: boolean;
  conversationId: string | null;

  // Entity context
  selectedEntity: SelectedEntity | null;
  setSelectedEntity: (entity: SelectedEntity | null) => void;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  startNewConversation: () => void;

  // Toast callback (to be set by parent)
  showToast: (props: AgentToastProps) => void;
}

const AIAgentContext = createContext<AIAgentContextType | undefined>(undefined);

interface AIAgentProviderProps {
  children: ReactNode;
  onShowToast?: (props: AgentToastProps) => void;
}

export function AIAgentProvider({ children, onShowToast }: AIAgentProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Show toast notification
  const showToast = useCallback(
    (props: AgentToastProps) => {
      if (onShowToast) {
        onShowToast(props);
      }
    },
    [onShowToast]
  );

  // Send message to agent
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      // Add user message
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      // Prepare assistant message with thinking state
      const assistantMessageId = crypto.randomUUID();
      let streamedContent = '';
      let hasReceivedContent = false;

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId,
          role: 'assistant',
          content: 'Nachdenken...',
          isStreaming: true,
          isThinking: true,
          createdAt: new Date(),
        },
      ]);

      // Build date/time context for relative date resolution
      const now = new Date();
      const dateFormatter = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        weekday: 'long',
      });
      const dateContext = {
        nowIso: now.toISOString(),
        todayDate: now.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }), // YYYY-MM-DD format
        timezone: 'Europe/Berlin',
        weekday: dateFormatter.format(now),
      };

      try {
        await sendAgentMessage({
          conversationId: conversationId || undefined,
          message: content.trim(),
          context: {
            currentRoute: location.pathname,
            selectedEntityId: selectedEntity?.id,
            selectedEntityType: selectedEntity?.type,
            dateTime: dateContext,
          },
          onMessageStart: (data) => {
            setConversationId(data.conversationId);
          },
          onContentDelta: (delta) => {
            // First content: switch from thinking to streaming
            if (!hasReceivedContent) {
              hasReceivedContent = true;
              streamedContent = delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, content: streamedContent, isThinking: false }
                    : m
                )
              );
            } else {
              streamedContent += delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId ? { ...m, content: streamedContent } : m
                )
              );
            }
          },
          onToolCallStart: (toolCallId, name) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? {
                      ...m,
                      toolCalls: [
                        ...(m.toolCalls || []),
                        { id: toolCallId, name, status: 'pending' as const },
                      ],
                    }
                  : m
              )
            );
          },
          onToolResult: async (toolCallId, _name, result: ToolCallResult) => {
            // Update tool call status
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? {
                      ...m,
                      toolCalls: m.toolCalls?.map((tc: ToolCall) =>
                        tc.id === toolCallId
                          ? {
                              ...tc,
                              status: result.success ? ('success' as const) : ('failed' as const),
                              result,
                            }
                          : tc
                      ),
                    }
                  : m
              )
            );

            // Dispatch refresh event for UI updates
            if (result.invalidateQueries && result.invalidateQueries.length > 0) {
              dispatchAgentRefresh({
                actionType: _name,
                success: result.success,
                invalidateQueries: result.invalidateQueries,
                entityType: _name.startsWith('TODO_') ? 'todo' : _name.startsWith('LEAD_') ? 'lead' : undefined,
                entityId: result.data?.todoId || result.data?.leadId,
              });
            }

            // Handle client-side action execution
            if (result.data?.executeOnClient && result.data?.action === 'TOUR_START') {
              const tourId = result.data.params?.tour_id as string;
              if (tourId) {
                // Close sidebar before starting tour
                setIsOpen(false);
                // Small delay to let the sidebar close and message appear first
                setTimeout(() => {
                  dispatchAgentTourStart(tourId);
                }, 500);
              }
            }

            // Handle implicit navigation from action results
            if (result.navigateTo) {
              navigate(result.navigateTo);

              // After navigation, dispatch focus event if present
              if (result.focus) {
                // Small delay to let the page render first
                setTimeout(() => {
                  dispatchAgentFocus(result.focus!);
                }, 100);
              }
            }
          },
          onMessageEnd: () => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId ? { ...m, isStreaming: false } : m
              )
            );
          },
          onError: (error) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, content: `Fehler: ${error}`, isStreaming: false, isThinking: false }
                  : m
              )
            );
          },
        });
      } catch (error) {
        let errorMessage: string;
        if (error instanceof RateLimitError) {
          const { usage } = error;
          if (usage.daily_exceeded) {
            errorMessage = `⚠️ Tageslimit erreicht: ${usage.daily_count}/${usage.daily_limit} Anfragen heute. Bitte morgen wieder versuchen.`;
          } else {
            errorMessage = `⚠️ Monatslimit erreicht: ${usage.monthly_count}/${usage.monthly_limit} Anfragen diesen Monat.`;
          }
        } else {
          errorMessage = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten';
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, content: errorMessage, isStreaming: false, isThinking: false, isError: true }
              : m
          )
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      conversationId,
      isLoading,
      location.pathname,
      selectedEntity,
      navigate,
    ]
  );

  // Start new conversation
  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
  }, []);

  return (
    <AIAgentContext.Provider
      value={{
        isOpen,
        setIsOpen,
        messages,
        isLoading,
        conversationId,
        selectedEntity,
        setSelectedEntity,
        sendMessage,
        startNewConversation,
        showToast,
      }}
    >
      {children}
    </AIAgentContext.Provider>
  );
}

export function useAIAgent() {
  const context = useContext(AIAgentContext);
  if (!context) {
    throw new Error('useAIAgent must be used within AIAgentProvider');
  }
  return context;
}
